import {Box, Button, Grid, Typography} from '@mui/material';
import React, {useCallback} from 'react';
import FormDateRange from '../shared/FormDateRange';
import FormCheckbox from '../shared/FormCheckbox';
import {SessionTypes} from '../../enums';
import {Control, FieldValues, useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';

interface TeamSessionsFilterForm {
  dateRange: {
    startDate: Date | null;
    endDate: Date | null;
    key: 'selection';
  } | null;
  sessionTypes: Record<string, boolean>;
}

interface Props {
  defaultStartDate?: Date;
  defaultEndDate?: Date;
  onDownload?: () => void;
  onReset?: () => void;
  onSubmit: ({
    startDate,
    endDate,
    sessionTypes,
  }: {
    startDate: Date;
    endDate: Date;
    sessionTypes: string[];
  }) => void;
}

const TeamSessionsFilter: React.FC<Props> = ({
  onDownload,
  onReset,
  onSubmit,
  defaultStartDate,
  defaultEndDate,
}) => {
  const {t} = useTranslation();

  const {handleSubmit, watch, reset, control} =
    useForm<TeamSessionsFilterForm>({
      defaultValues: {
        dateRange:
          defaultStartDate && defaultEndDate
            ? {
                startDate: defaultStartDate,
                endDate: defaultEndDate,
                key: 'selection',
              }
            : null,
        sessionTypes: {},
      },
    });

  const handleSubmitCallback = useCallback(
    async (data: TeamSessionsFilterForm) => {
      const sessionTypes = Object.keys(data.sessionTypes).filter(
        sessionType => data.sessionTypes[sessionType],
      );

      onSubmit({
        startDate: data.dateRange?.startDate as Date,
        endDate: data.dateRange?.endDate as Date,
        sessionTypes,
      });
    },
    [onSubmit],
  );

  const handleReset = useCallback(() => {
    reset();
    if (onReset) {
      onReset();
    }
  }, [reset, onReset]);

  return (
    <form onSubmit={handleSubmit(handleSubmitCallback)}>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <FormDateRange
            label={t('sessionFilter.sessionDates') as string}
            control={control as unknown as Control<FieldValues>}
            name="dateRange"
            sx={{mb: 2}}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <Box sx={{mb: 2, position: 'relative', top: -10}}>
            <Typography variant="caption" color="text.secondary">
              {t('sessionFilter.sessionTypes')}
            </Typography>
            <Box sx={{display: 'flex', flexDirection: 'row', flexWrap: 'wrap'}}>
              <FormCheckbox
                control={control as unknown as Control<FieldValues>}
                name={`sessionTypes.${SessionTypes.game}`}
                label={`${t('sessionFilter.game')}`}
                sx={{mr: 2}}
              />
              <FormCheckbox
                control={control as unknown as Control<FieldValues>}
                name={`sessionTypes.${SessionTypes.practice}`}
                label={`${t('sessionFilter.practice')}`}
                sx={{mr: 2}}
              />
              <FormCheckbox
                control={control as unknown as Control<FieldValues>}
                name={`sessionTypes.${SessionTypes.skills}`}
                label={`${t('sessionFilter.skills')}`}
                sx={{mr: 2}}
              />
            </Box>
          </Box>
        </Grid>
      </Grid>
      <Box
        sx={{
          mb: 2,
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 2,
        }}>
        <Box>
          <Button variant="outlined" onClick={handleReset} sx={{mr: 2}}>
            {t('general.reset')}
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={!watch('dateRange')}>
            {t('general.submit')}
          </Button>
        </Box>
        {onDownload && (
          <Button variant="outlined" onClick={onDownload}>
            {t('general.download')}
          </Button>
        )}
      </Box>
    </form>
  );
};

export default TeamSessionsFilter;
